import React from 'react';
import { X, Sparkles, TrendingUp, Trophy, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { getBrazilianStatus } from '../../../utils/detectBrazil';
import { multiplasModalPtBR } from '../../mappings/multiplas-modal.pt-br';
import { useAddToChat } from '../../chat/chatContext';

const SportIcon = ({ sport }) => {
  switch (sport?.toLowerCase()) {
    case 'baseball':
    case 'football':
    case 'nfl':
    case 'ncaaf':
    case 'nba':
    case 'nhl':
      return <Trophy className="w-4 h-4 text-white/50" />;
    default:
      return <Sparkles className="w-4 h-4 text-white/50" />;
  }
};

const BetCard = ({ bet, index, t }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.2 }}
    >
      <div className="relative bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-4 hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 group">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase">
            {t(bet.betType)}
          </h4>
          <div className="flex items-center gap-2">
            {bet.numericConfidence && (
              <span className="font-mono text-sm text-emerald-400/80 group-hover:text-emerald-400/90">
                {/* TODO-NFL-FIX: Add '%' here since numericConfidence is stored without it */}
                {bet.numericConfidence}%
              </span>
            )}
            <span className={`px-2 py-0.5 rounded text-[10px] font-medium tracking-wider uppercase ${
              bet.confidence === 'HIGH'
                ? 'bg-emerald-500/10 text-emerald-400/70 border border-emerald-500/20'
                : bet.confidence === 'LOW'
                ? 'bg-red-500/10 text-red-400/70 border border-red-500/20'
                : 'bg-violet-500/10 text-violet-400/70 border border-violet-500/20'
            }`}>
              {t(bet.confidence)}
            </span>
          </div>
        </div>
        <div className="font-mono text-base text-white/80 group-hover:text-white/90">
          {bet.selection}
        </div>
        {bet.reasoning && (
          <p className="mt-2 text-xs text-white/40 leading-relaxed">
            {bet.reasoning}
          </p>
        )}
      </div>
    </motion.div>
  );
};

const LoadingState = ({ t }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="flex flex-col items-center justify-center py-12"
  >
    <div className="relative mb-6">
      <div className="w-16 h-16 rounded-full border-2 border-white/[0.05] backdrop-blur-sm" />
      <div className="absolute inset-0 w-16 h-16 rounded-full border-2 border-emerald-500/30 border-t-emerald-500/80 animate-spin" />
      <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-violet-400/50" />
    </div>
    <div className="text-center">
      <p className="text-xs font-medium tracking-[0.3em] text-white/30 uppercase mb-2">
        {t("Analyzing game data...")}
      </p>
      <p className="text-[11px] text-white/20">
        {t("Calculating probabilities...")}
      </p>
    </div>
  </motion.div>
);

const ErrorState = ({ error, t }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="py-8"
  >
    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
      <p className="text-xs text-red-400/70">
        {error || t('Failed to get predictions')}
      </p>
    </div>
  </motion.div>
);

const MultiplasModal = ({ open, onClose, loading, prediction, error, gameData, sport }) => {
  const navigate = useNavigate();
  const handleAddToChat = useAddToChat();

  // Check if user is Brazilian
  const isBrazilian = getBrazilianStatus();

  // Translation function
  const t = (text) => {
    if (!isBrazilian) return text;
    return multiplasModalPtBR[text] || text;
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handler to send xPicks predictions to chat for deeper analysis
  const handleAnalyzeInChat = async () => {
    if (!prediction || !gameData) return;

    // Format sport name
    const sportNames = {
      baseball: 'baseball',
      football: isBrazilian ? 'futebol' : 'soccer',
      nfl: 'NFL',
      ncaaf: 'NCAA Football',
      nba: 'NBA',
      nhl: 'NHL'
    };
    const sportName = sportNames[sport?.toLowerCase()] || sport;

    // Build bets list
    const betsList = prediction.bets?.map((bet, index) => {
      const confidence = bet.numericConfidence ? `${bet.numericConfidence}%` : bet.confidence;
      return `${index + 1}. ${bet.betType}: ${bet.selection} - ${confidence} ${bet.confidence ? `(${bet.confidence})` : ''}`;
    }).join('\n') || '';

    const message = isBrazilian
      ? `Analise estas recomendações de apostas xPicks para ${sportName}:

${gameData.awayTeam} @ ${gameData.homeTeam}

Top ${prediction.bets?.length || 5} Apostas Recomendadas:
${betsList}

${prediction.summary ? `\nResumo: ${prediction.summary}\n` : ''}
Você pode fornecer uma análise mais profunda dessas recomendações de apostas? Considere fatores como forma recente, estatísticas head-to-head e quaisquer insights adicionais.`
      : `Analyze these xPicks betting recommendations for ${sportName}:

${gameData.awayTeam} @ ${gameData.homeTeam}

Top ${prediction.bets?.length || 5} Recommended Bets:
${betsList}

${prediction.summary ? `\nSummary: ${prediction.summary}\n` : ''}
Can you provide a deeper analysis of these betting recommendations? Consider factors like recent form, head-to-head stats, and any additional insights.`;

    await handleAddToChat(message);
    onClose(); // Close modal
    navigate('/chat');
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <AnimatePresence mode="wait">
      {open && (
        <motion.div
          key="modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md"
          onClick={handleBackdropClick}
        >
          <motion.div
            key="modal-content"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="relative max-w-2xl w-full mx-4 bg-black/90 backdrop-blur-lg border border-white/10 rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
          <div className="relative bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02] border-b border-white/[0.05] px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SportIcon sport={sport} />
                <div>
                  <h2 className="text-xs font-medium tracking-[0.3em] text-white/70 uppercase">
                    {t("xPicks Analysis")}
                  </h2>
                  {gameData && (
                    <p className="text-[11px] text-white/50 mt-1">
                      {gameData.homeTeam} vs {gameData.awayTeam}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-md hover:bg-white/[0.05] transition-colors"
              >
                <X className="w-4 h-4 text-white/40 hover:text-white/60" />
              </button>
            </div>
          </div>

          <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
            <AnimatePresence mode="wait">
              {loading && <LoadingState t={t} />}
              {error && !loading && <ErrorState error={error} t={t} />}
              {prediction && !loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {/* Analyze in Chat Button - Moved to top */}
                  <div className="mb-4">
                    <button
                      onClick={handleAnalyzeInChat}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 border border-violet-400/50 hover:border-violet-300 transition-all duration-200 group"
                    >
                      <MessageCircle className="w-3.5 h-3.5 text-white" />
                      <span className="text-xs font-medium tracking-[0.15em] text-white uppercase">
                        {t("Analyze in Chat")}
                      </span>
                    </button>
                  </div>

                  <div className="mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-400/70" />
                    <h3 className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase">
                      {t("Top")} {prediction.bets?.length || 5} {t("High Probability Bets")}
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {prediction.bets?.map((bet, index) => (
                      <BetCard key={index} bet={bet} index={index} t={t} />
                    ))}
                  </div>
                  {prediction.summary && (
                    <div className="mt-6 p-4 bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02] border border-white/[0.05] rounded-lg">
                      <p className="text-[11px] font-medium tracking-wider text-white/30 uppercase mb-2">
                        {t("Analysis")}
                      </p>
                      <p className="text-xs text-white/50 leading-relaxed">
                        {prediction.summary}
                      </p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default MultiplasModal;